import type {
  CargoProblemWorkspaceMessageItem,
  CargoProblemWorkspaceMessagesResponse,
  CargoProblemWorkspaceStreamItem,
} from '@magimp/shared';

/**
 * @aiPurpose stable per-row identity for the conversation. Message ids and
 *   event ids come from different tables, so the `type` prefix keeps the two
 *   families in separate namespaces.
 */
export function cargoProblemStreamKey(item: CargoProblemWorkspaceStreamItem): string {
  return `${item.type}:${item.id}`;
}

/**
 * @aiPurpose flattens the cursor pages into one chronological conversation.
 * @aiInvariant page 0 is the NEWEST page and every later page is older, so the
 *   pages are walked in reverse; inside a page the server already orders
 *   oldest→newest.
 * @aiInvariant emits every row at most once. A refetch of page 0 slides its
 *   window forward, which can re-list a row an older page still holds — the
 *   de-duplication is what keeps a re-read from doubling the conversation.
 * @aiInvariant the server order IS the causal order: messages and workflow
 *   cards share one monotonic timeline counter that `createdAt` cannot
 *   reproduce, because two rows written inside the same millisecond carry the
 *   same timestamp. Re-sorting here by timestamp (or by id) would reorder those
 *   rows — the causal-order defect fixed in #2865. The walk therefore only
 *   concatenates what the server already ordered.
 */
export function mergeCargoProblemStream(
  pages: readonly CargoProblemWorkspaceMessagesResponse[],
): CargoProblemWorkspaceStreamItem[] {
  const seen = new Set<string>();
  const merged: CargoProblemWorkspaceStreamItem[] = [];
  for (let index = pages.length - 1; index >= 0; index -= 1) {
    for (const item of pages[index]?.items ?? []) {
      const key = cargoProblemStreamKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(item);
    }
  }
  return merged;
}

/** Human messages only — the read pointer is advanced by a message id. */
export function cargoProblemStreamMessages(
  items: readonly CargoProblemWorkspaceStreamItem[],
): CargoProblemWorkspaceMessageItem[] {
  return items.filter((item): item is CargoProblemWorkspaceMessageItem => item.type === 'MESSAGE');
}

/** The stream row that published a material, or `null` while its page is unloaded. */
export function findCargoProblemMaterialSource(
  items: readonly CargoProblemWorkspaceStreamItem[],
  fileId: string,
): string | null {
  for (const item of items) {
    const attachments =
      item.type === 'MESSAGE'
        ? item.attachments
        : 'attachments' in item
          ? item.attachments
          : item.kind === 'FILE_UPLOADED'
            ? [item.file]
            : [];
    if (attachments.some((file) => file.id === fileId)) return cargoProblemStreamKey(item);
  }
  return null;
}
